"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ExternalLink, CheckCircle2, XCircle, Loader2, ChevronDown, Info, LogIn, Cloud, HardDrive, Cookie } from "@/lib/icons"
import { sourceHasNoCredentials, type SourceConfig } from "@/lib/source-store"
import { useBooruCredentialsStore } from "@/lib/booru-credentials-store"
import { useAuth } from "@/hooks/use-auth"
import { toast } from "sonner"
import { LoginPromptDialog } from "@/components/auth/login-prompt-dialog"
import { useSettingsStore } from "@/lib/settings-store"
import { useTranslations } from "next-intl"

interface BooruAuthDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: SourceConfig
  onSave: (credentials: { username?: string; apiKey?: string }) => Promise<void>
  currentCredentials?: { username?: string; apiKey?: string }
}

export function BooruAuthDialog({
  open,
  onOpenChange,
  source,
  onSave,
  currentCredentials,
}: BooruAuthDialogProps) {
  const t = useTranslations("booruAuthDialog")
  const { isAuthenticated, user } = useAuth()
  const { setCredentials, getCredentials, dismissPrompt } = useBooruCredentialsStore()
  const { universalUsername, universalPassword, universalApiKey } = useSettingsStore()
  
  const [username, setUsername] = useState("")
  const [apiKey, setApiKey] = useState("")
  const [password, setPassword] = useState("") // For Sankaku login
  const [applySankakuToOther, setApplySankakuToOther] = useState(false) // Share credentials with other Sankaku
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<"success" | "error" | null>(null)
  const [testMessage, setTestMessage] = useState("")
  const [saving, setSaving] = useState(false)
  const [syncingToAccount, setSyncingToAccount] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const [extractingCookies, setExtractingCookies] = useState(false)
  const [cookieWindow, setCookieWindow] = useState<Window | null>(null)

  // Sources like Erome have no credential system at all — this dialog has
  // nothing useful to offer them, so it must never open no matter which of
  // the five call sites triggered it.
  //
  // This used to be a useEffect that called onOpenChange(false) when
  // `source.requiresAuth === false`. Two problems, both of which let the
  // "Connect to Erome" prompt keep appearing after it was supposedly fixed:
  // an effect only runs AFTER the dialog has already rendered and painted,
  // so it flashes on screen before closing itself; and it trusted
  // `source.requiresAuth`, which comes from a persisted/merged SourceConfig
  // and so reads as undefined for anyone still carrying a config saved
  // before that field was added — the exact case where the guard needed to
  // fire. sourceHasNoCredentials() consults a hardcoded id list that stored
  // data can't invalidate, and bailing during render means there is no frame
  // in which the dialog exists at all.
  const hasNoCredentialSystem = sourceHasNoCredentials(source)

  // Load credentials when dialog opens or source changes
  useEffect(() => {
    if (open) {
      // Load existing credentials from localStorage for this specific booru
      const existingCreds = getCredentials(source.id)
      setUsername(currentCredentials?.username || existingCreds?.username || "")
      setApiKey(currentCredentials?.apiKey || existingCreds?.apiKey || "")
      
      // Reset test results
      setTestResult(null)
      setTestMessage("")
      
      // Also try to load from account if logged in
      if (isAuthenticated && user) {
        loadCredentialsFromAccount()
      }
    } else {
      // Reset state when dialog closes
      setTestResult(null)
      setTestMessage("")
      setShowInstructions(false)
    }
  }, [open, source.id, isAuthenticated, user])

  const loadCredentialsFromAccount = async () => {
    try {
      const response = await fetch(`/api/booru/credentials?booruId=${source.id}`)
      if (response.ok) {
        const data = await response.json()
        if (data.credentials) {
          setUsername(data.credentials.username || "")
          setApiKey(data.credentials.apiKey || "")
          console.log('[v0] Loaded credentials from account')
        }
      }
    } catch (error) {
      console.error('[v0] Error loading credentials from account:', error)
    }
  }

  const requiresAuth = source.auth.type === "api_key"
  const optionalAuth = source.auth.type === "optional_api_key"
  const isSankaku = source.id === "sankaku" || source.id === "idolsankaku"
  const isBasicAuth = source.auth.type === "basic" // Username/password authentication
  const otherSankakuSite = source.id === "sankaku" ? "idolsankaku" : source.id === "idolsankaku" ? "sankaku" : null
  // Site names, so they read the same in every locale.
  const otherSankakuSiteName = otherSankakuSite === "sankaku" ? "Sankaku Complex" : "Idol Complex"

  // Get instructions based on booru
  const getInstructions = () => {
    switch (source.id) {
      case "sankaku":
      case "idolsankaku":
        return {
          steps: [
            "Enter your Sankaku username and password",
            "Click 'Login' to authenticate",
            "Your credentials will be exchanged for an access token",
            "Optionally apply the same credentials to the other Sankaku site",
          ],
          helpUrl: source.id === "sankaku" ? "https://www.sankakucomplex.com" : "https://www.idolcomplex.com",
          benefits: "Login is required to access Sankaku content. Your password is only used to obtain an access token and is not stored.",
        }
      case "danbooru":
        return {
          steps: [
            "Click 'Visit Danbooru' below",
            "Create an account or log in",
            "Go to your Profile page",
            "Scroll to 'API Key' section",
            "Copy your API key",
            "Paste it in the field above",
          ],
          helpUrl: "https://danbooru.donmai.us/profile",
          benefits: "Adding your own API key allows higher rate limits, access to favorites, and NSFW content. This helps when posts don't load properly.",
        }
      case "gelbooru":
        return {
          steps: [
            "Click 'Visit Gelbooru' below",
            "Create an account or log in",
            "Go to Account → Options",
            "Find 'API Key' section",
            "Copy your User ID (numeric) and API key",
            "Paste them in the fields above",
          ],
          helpUrl: "https://gelbooru.com/index.php?page=account&s=options",
          benefits: "Adding your own API key allows unlimited searches, better reliability, and access to all content.",
        }
      case "civitai":
        return {
          steps: [
            "Click 'Visit Civitai' below and sign in to your own Civitai account (a separate account just for this key is fine — you don't need to use your main one)",
            "Go to Account Settings → API Keys → Create API Key",
            "Under permissions, check only 'Media & Posts: Read' — leave everything else unchecked",
            "Copy the generated key and paste it in the field above",
          ],
          helpUrl: "https://civitai.com/user/account",
          benefits: "Civitai already works without a key — we use a shared platform key by default. Only add your own if searches start failing or feel rate-limited; it's stored on your account and used as a fallback, not a login. Only grant 'Media & Posts: Read' — that's all this integration ever uses, even if your key has broader access.",
        }
      case "e621":
        return {
          steps: [
            "Click 'Visit e621' below",
            "Create an account or log in",
            "Go to Account → Manage API Access",
            "Generate a new API key",
            "Copy your username and API key",
            "Paste them in the fields above",
          ],
          helpUrl: "https://e621.net/users/home",
          benefits: "API keys are required for searching. Without them, you cannot use e621.",
        }
      case "safebooru":
        return {
          steps: [
            "Click 'Visit Safebooru' below",
            "Create an account or log in",
            "Go to Account → Options",
            "Find 'API Key' section",
            "Copy your User ID (numeric) and API key",
            "Paste them in the fields above",
          ],
          helpUrl: "https://safebooru.org/index.php?page=account&s=options",
          benefits: "Adding your own API key allows unlimited searches, better reliability, and access to all content.",
        }
      default:
        return {
          steps: [
            "Visit the site using the button below",
            "Create an account or log in",
            "Find your API key in account settings",
            "Copy and paste it above",
          ],
          helpUrl: source.endpoints.search.replace(/\/.*$/, ""),
          benefits: "Adding your own API key typically allows higher rate limits, better reliability, and access to more features.",
        }
    }
  }

  const instructions = getInstructions()

  const handleUseUniversalCredentials = () => {
    if (universalUsername) setUsername(universalUsername)
    if (universalPassword) setPassword(universalPassword)
    if (universalApiKey) setApiKey(universalApiKey)
    
    if (universalUsername || universalPassword || universalApiKey) {
      toast.success(t("universalCredentialsApplied"))
    } else {
      toast.error(t("noUniversalCredentialsSetGoToSettingsP"))
    }
  }

  const hasUniversalCredentials = !!(universalUsername || universalPassword || universalApiKey)

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    setTestMessage("")

    try {
      // Make a test API call
      const params = new URLSearchParams({
        site: source.id,
        tags: "rating:safe",
        limit: "1",
      })

      if (username) params.append("username", username)
      if (apiKey) params.append("apiKey", apiKey)

      const response = await fetch(`/api/booru/test-auth?${params}`)
      const data = await response.json()

      if (response.ok && data.success) {
        setTestResult("success")
        setTestMessage(data.message || "Connection successful!")
      } else {
        // Check if it's a Cloudflare issue
        if (data.isCloudflare) {
          setTestResult("error")
          setTestMessage("⚠️ Testing blocked by Cloudflare protection. Your credentials may still work - save them and try searching to verify.")
        } else {
          setTestResult("error")
          setTestMessage(data.error || "Connection failed. Please check your credentials.")
        }
      }
    } catch (error) {
      setTestResult("error")
      setTestMessage("Failed to test connection. Please try again.")
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Special handling for Sankaku sites - login to get bearer token
      if (isSankaku && username && password) {
        const sitesToLogin = [source.id]
        if (applySankakuToOther && otherSankakuSite) {
          sitesToLogin.push(otherSankakuSite)
        }

        const response = await fetch('/api/booru/sankaku-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            password,
            sites: sitesToLogin,
          }),
        })

        const data = await response.json() as {
          success: boolean
          results: Record<string, { success: boolean; token?: string; error?: string }>
        }

        if (!data.success) {
          toast.error(t("loginFailedPleaseCheckYourCredentials"))
          setSaving(false)
          return
        }

        // Save tokens to localStorage
        for (const [siteId, result] of Object.entries(data.results)) {
          if (result.success && result.token) {
            setCredentials(siteId, { 
              username, 
              apiKey: result.token // Store bearer token as apiKey
            })
            console.log(`[v0] Saved token for ${siteId}`)
          }
        }

        // If logged in, also sync to account
        if (isAuthenticated && user) {
          setSyncingToAccount(true)
          try {
            for (const [siteId, result] of Object.entries(data.results)) {
              if (result.success && result.token) {
                await fetch('/api/booru/credentials', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    booruId: siteId,
                    username,
                    apiKey: result.token,
                  }),
                })
              }
            }
            console.log('[v0] Sankaku credentials synced to account')
            toast.success(t("loggedInToLengthSankakuSiteS", { length: sitesToLogin.length }))
          } catch (error) {
            console.error('[v0] Error syncing to account:', error)
            toast.success(t("loggedInLocallyAccountSyncFailed"))
          } finally {
            setSyncingToAccount(false)
          }
        } else {
          const successCount = Object.values(data.results).filter(r => r.success).length
          toast.success(t("loggedInToSuccessCountSankakuSiteS", { successCount: successCount }))
        }
      } else {
        // Standard API key save for other boorus
        // Always save to localStorage first (works without login)
        setCredentials(source.id, { username, apiKey })
        console.log('[v0] Credentials saved to localStorage')
        
        // If logged in, also sync to account
        if (isAuthenticated && user) {
          setSyncingToAccount(true)
          try {
            const response = await fetch('/api/booru/credentials', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                booruId: source.id,
                username,
                apiKey,
              }),
            })

            if (response.ok) {
              console.log('[v0] Credentials synced to account')
              // Record that the account mirror succeeded so the settings modal
              // can show "Account (synced)" instead of always claiming local-only.
              setCredentials(source.id, { username, apiKey, syncedToAccount: true })
              toast.success(t("credentialsSavedToYourAccount"))
            } else {
              console.error('[v0] Failed to sync to account')
              toast.success(t("credentialsSavedLocallyAccountSyncFailed"))
            }
          } catch (error) {
            console.error('[v0] Error syncing to account:', error)
            toast.success(t("credentialsSavedLocallyAccountSyncFailed"))
          } finally {
            setSyncingToAccount(false)
          }
        } else {
          toast.success(t("credentialsSavedLocallyOnThisDevice"))
        }
      }
      
      // Call the onSave callback if provided (for compatibility)
      if (onSave) {
        await onSave({ username, apiKey })
      }
      
      onOpenChange(false)
    } catch (error) {
      console.error('[v0] Error saving credentials:', error)
      toast.error(t("failedToSaveCredentials"))
    } finally {
      setSaving(false)
    }
  }

  const handleLogin = () => {
    // Open login dialog
    setShowLoginDialog(true)
  }

  const handleLoginSuccess = () => {
    // After successful login, reload credentials from account
    loadCredentialsFromAccount()
    toast.success(t("loggedInYourCredentialsWillNowSyncToYou"))
  }

  const handleDontAskAgain = () => {
    dismissPrompt(source.id)
    toast.info(t("wonTAskAgainForNameReEnableThisAnyti", { name: source.name }))
    onOpenChange(false)
  }

  const handleVisitSite = () => {
    window.open(instructions.helpUrl, "_blank", "width=1000,height=800")
  }

  const handleGetCookies = () => {
    setExtractingCookies(true)
    
    // Open the site in a new window
    const siteUrl = source.homepage || instructions.helpUrl
    const newWindow = window.open(
      siteUrl,
      "_blank",
      "width=1000,height=800,toolbar=yes,location=yes,menubar=yes"
    )
    
    if (newWindow) {
      setCookieWindow(newWindow)
      toast.info(t("logInToTheSiteThenClickExtractCookies"))
    } else {
      toast.error(t("failedToOpenWindowPleaseAllowPopupsForT"))
      setExtractingCookies(false)
    }
  }

  const handleExtractCookies = async () => {
    try {
      // Note: Due to browser security restrictions, we can't directly access cookies from another domain
      // Instead, we'll guide the user to manually copy cookies or use a browser extension
      
      toast.info(t("cookieExtractionFeatureComingSoonForNow"))
      
      // Close the cookie window if it's still open
      if (cookieWindow && !cookieWindow.closed) {
        cookieWindow.close()
      }
      
      setExtractingCookies(false)
      setCookieWindow(null)
      
      // Show instructions for manual cookie extraction
      setShowInstructions(true)
    } catch (error) {
      console.error('[v0] Error extracting cookies:', error)
      toast.error(t("failedToExtractCookiesPleaseTryManualEnt"))
      setExtractingCookies(false)
    }
  }

  const handleCancelCookieExtraction = () => {
    if (cookieWindow && !cookieWindow.closed) {
      cookieWindow.close()
    }
    setExtractingCookies(false)
    setCookieWindow(null)
  }

  // Render nothing at all for a source with no credential system (see the
  // hasNoCredentialSystem note above). Placed after every hook so the Rules
  // of Hooks still hold, but before any dialog markup exists.
  if (hasNoCredentialSystem) return null

  return (
    <>
      <LoginPromptDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        feature="sync your API credentials across devices"
        onSuccess={handleLoginSuccess}
      />
      <Dialog open={open && source.requiresAuth !== false} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("connectToName", { name: source.name })}</DialogTitle>
          <DialogDescription>
            {source.id === "civitai"
              ? "No login needed — just paste an API key. This works out of the box on our shared key; add your own only as a rate-limit fallback."
              : requiresAuth
              ? "Authentication is required to use this site."
              : "Add your own API credentials to improve reliability and access more features. This is especially helpful when posts don't load properly."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Storage Info Alert */}
          {!isAuthenticated ? (
            <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20">
              <HardDrive className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertTitle className="text-amber-900 dark:text-amber-100">{t("savingLocally")}</AlertTitle>
              <AlertDescription className="text-amber-800 dark:text-amber-200 space-y-3">
                <p className="text-sm">
                  {t("yourCredentialsWillBeSavedOnThisDeviceOn")}
                </p>
                <div className="space-y-2">
                  <p className="text-sm font-semibold">{t("whyLogin")}</p>
                  <ul className="text-xs space-y-1 list-disc list-inside">
                    <li>{t("syncCredentialsAcrossAllYourDevices")}</li>
                    <li>{t("automaticBackupNeverLoseYourAPIKeys")}</li>
                    <li>{t("secureCloudStorageWithEncryption")}</li>
                  </ul>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleLogin}
                    className="w-full mt-2 bg-white dark:bg-gray-900 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    {t("loginToSaveToAccount")}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20">
              <Cloud className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle className="text-green-900 dark:text-green-100">{t("syncingToAccount")}</AlertTitle>
              <AlertDescription className="text-green-800 dark:text-green-200">
                <p className="text-sm">
                  {t("yourCredentialsWillBeSavedToYourAccountA")}
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Benefits Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">{instructions.benefits}</AlertDescription>
          </Alert>

          {/* Use Universal Credentials Button */}
          {hasUniversalCredentials && (
            <Button 
              variant="outline" 
              onClick={handleUseUniversalCredentials}
              className="w-full"
            >
              {t("useUniversalCredentials")}
            </Button>
          )}

          {/* Username/User ID Field (if needed) */}
          {(isBasicAuth || source.auth.userIdParam || source.id === "danbooru" || source.id === "e621" || source.id === "e926") && (
            <div className="space-y-2">
              <Label htmlFor="username">
                {isSankaku ? "Username" : source.auth.userIdParam === "user_id" ? "User ID" : "Username"} {(requiresAuth || isBasicAuth) && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="username"
                type="text"
                placeholder={isSankaku ? "Your Sankaku username" : source.auth.userIdParam === "user_id" ? "Your numeric user ID" : "Your username"}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
          )}

          {/* Password Field (for basic auth sites) */}
          {isBasicAuth && (
            <div className="space-y-2">
              <Label htmlFor="password">
                {t("password")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder={isSankaku ? t("yourSankakuPassword") : t("yourPassword")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {isSankaku && (
                <p className="text-xs text-muted-foreground">
                  {t("yourPasswordIsOnlyUsedToObtainAnAccessT")}
                </p>
              )}
            </div>
          )}

          {/* Sankaku Credential Sharing Checkbox */}
          {isSankaku && otherSankakuSite && (
            <div className="flex items-start space-x-2 rounded-lg border p-3 bg-muted/50">
              <input
                type="checkbox"
                id="applySankakuToOther"
                checked={applySankakuToOther}
                onChange={(e) => setApplySankakuToOther(e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="applySankakuToOther" className="cursor-pointer font-normal">
                  {t("alsoLoginToSiteWithTheseCredentials", { site: otherSankakuSiteName })}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("ifYouUseTheSameAccountForBothSankakuSit")}
                </p>
              </div>
            </div>
          )}

          {/* API Key Field (for non-basic auth sites) */}
          {!isBasicAuth && (
            <div className="space-y-2">
              <Label htmlFor="apiKey">
                {t("apiKey")} {requiresAuth && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="apiKey"
                type="password"
                placeholder={t("yourAPIKey")}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>
          )}

          {/* Visit Site Button */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleVisitSite} className="flex-1">
              <ExternalLink className="h-4 w-4 mr-2" />
              {t("visitName", { name: source.name })}
            </Button>
            {source.auth.type === "cookie" && (
              <Button 
                variant="outline" 
                onClick={extractingCookies ? handleCancelCookieExtraction : handleGetCookies}
                className="flex-1"
              >
                <Cookie className="h-4 w-4 mr-2" />
                {extractingCookies ? t("cancel") : t("getCookies")}
              </Button>
            )}
          </div>

          {/* Cookie Extraction Status */}
          {extractingCookies && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/20">
              <Cookie className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertTitle className="text-blue-900 dark:text-blue-100">{t("cookieExtractionActive")}</AlertTitle>
              <AlertDescription className="text-blue-800 dark:text-blue-200 space-y-2">
                <p className="text-sm">
                  {t("n1LogInToNameInTheOpenedWindow", { name: source.name })}
                </p>
                <p className="text-sm">
                  {t("n2CompleteAnyVerificationSteps")}
                </p>
                <p className="text-sm">
                  {t("n3ClickExtractCookiesBelowWhenLoggedIn")}
                </p>
                <Button 
                  onClick={handleExtractCookies}
                  size="sm"
                  className="w-full mt-2"
                >
                  {t("extractCookies")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Instructions Collapsible */}
          <Collapsible open={showInstructions} onOpenChange={setShowInstructions}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span>{t("howToGetYourAPIKey")}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showInstructions ? "rotate-180" : ""}`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              <div className="rounded-lg border p-4 space-y-2">
                {instructions.steps.map((step, index) => (
                  <div key={index} className="flex gap-2 text-sm">
                    <span className="font-semibold text-muted-foreground">{index + 1}.</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Test Result */}
          {testResult && (
            <Alert variant={testResult === "success" ? "default" : "destructive"}>
              {testResult === "success" ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>{testMessage}</AlertDescription>
            </Alert>
          )}

          {/* Note about optional test */}
          {!testResult && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {t("testingIsOptionalAndMayFailDueToBotProt")}
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDontAskAgain}
              disabled={saving}
              className="text-muted-foreground hover:text-foreground mr-auto"
            >
              {t("donTAskAgainForName", { name: source.name })}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t("cancel")}
            </Button>
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || saving || (!username && !apiKey)}
              className="flex-1 sm:flex-none"
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("testing")}
                </>
              ) : (
                "Test Connection (Optional)"
              )}
            </Button>
            <Button onClick={handleSave} disabled={saving || testing || syncingToAccount || (requiresAuth && !apiKey) || (isBasicAuth && (!username || !password))}>
              {saving || syncingToAccount ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {syncingToAccount ? t("syncing") : isBasicAuth ? t("loggingIn") : t("saving")}
                </>
              ) : (
                <>{isBasicAuth ? 'Login' : `Save ${isAuthenticated ? 'to Account' : 'Locally'}`}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
